import React, { useEffect, useState } from "react";
import { API_URL } from "./config";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Row,
  Col,
  Card,
  Badge,
  Form,
  InputGroup,
} from "react-bootstrap";
import XGolifeDashboardComponent from "./components/XGolifeDashboardComponent";

const StoreInventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("product_id");
  const [sortDirection, setSortDirection] = useState("asc");

  const companyId = localStorage.getItem("company_id");
  const branchId = localStorage.getItem("branch_id");

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_URL}/productdefinition/full_products_definitions_by_company_id_branch_id/${companyId}/${branchId}`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }
        const data = await response.json();
        setItems(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (companyId && branchId) {
      fetchProducts();
    } else {
      setError("Company ID and Branch ID must be set in local storage.");
    }
  }, [companyId, branchId]);

  const filteredItems = items.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedItems = [...filteredItems].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (sortDirection === "asc") {
      return aValue > bValue ? 1 : -1;
    }
    return aValue < bValue ? 1 : -1;
  });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <XGolifeDashboardComponent>
      <div className="min-vh-10" style={{ width: "100%" }}>
        <div>
          <h2 style={{ marginTop: 30 }}>Inventory</h2>
        </div>
        <Card className="shadow-sm">
          <Card.Header className="bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Store Inventory</h4>
              <div className="d-flex gap-3">
                <Badge bg="primary" className="p-2">
                  Total Items: {items.length}
                </Badge>
                <InputGroup style={{ width: "300px" }}>
                  <InputGroup.Text>
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search inventory..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    {[
                      "Product ID",
                      "Product Code",
                      "Brand",
                      "Name",
                      "Type",
                      "Category",
                      "Sub Category",
                      "Sub Sub Category",
                      "Unit of Measure",
                      "Unit Size",
                      "Description",
                      "Condition",
                      "Cost",
                      "Selling Price",
                      "Shipping Days",
                      "Delivery Volume",
                      "Delivery Weight",
                      "Max Order",
                      "Min Order",
                      "Available Online",
                      "Enrolled By",
                      "cross_order_product_restriction_group_id",
                    ].map((header, index) => (
                      <th
                        key={index}
                        className="cursor-pointer"
                        onClick={() =>
                          handleSort(header.toLowerCase().replace(" ", "_"))
                        }
                      >
                        <div className="d-flex align-items-center">
                          {header}
                          {sortField ===
                            header.toLowerCase().replace(" ", "_") && (
                            <i
                              className={`ms-1 fas fa-sort-${
                                sortDirection === "asc" ? "up" : "down"
                              }`}
                            ></i>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item) => (
                    <tr key={item.product_id}>
                      <td>{item.product_id}</td>
                      <td>
                        <Badge bg="secondary">{item.product_code}</Badge>
                      </td>
                      <td>{item.product_brand || "N/A"}</td>
                      <td>{item.product_name}</td>
                      <td>
                        <Badge bg="info">{item.product_type || "N/A"}</Badge>
                      </td>
                      <td>{item.category || "N/A"}</td>
                      <td>{item.sub_category || "N/A"}</td>
                      <td>{item.sub_sub_category || "N/A"}</td>
                      <td>{item.unit_of_measure || "N/A"}</td>
                      <td>{item.unit_size || "N/A"}</td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: "400px" }}
                        >
                          {item.description_notes || "N/A"}
                        </div>
                      </td>
                      <td>{item.condition || "N/A"}</td>
                      <td>{item.product_value_cost || "N/A"}</td>
                      <td>{item.product_value_selling_price || "N/A"}</td>
                      <td>{item.shipping_days || "N/A"}</td>
                      <td>{item.delivery_volume || "N/A"}</td>
                      <td>{item.delivery_weight || "N/A"}</td>
                      <td>{item.max_order || "N/A"}</td>
                      <td>{item.min_order || "N/A"}</td>
                      <td>{item.available_online || "N/A"}</td>
                      <td>{item.enrolled_by || "N/A"}</td>
                      <td>{item.cross_order_product_restriction_group_id || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <style>
          {`
                    .cursor-pointer {
                        cursor: pointer;
                    }
                    .table th, .table td {
                        padding: 1rem;
                        vertical-align: middle;
                    }
                    .table-responsive {
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                    }
                `}
        </style>
      </div>
    </XGolifeDashboardComponent>
  );
};

export default StoreInventory;